/**
 * 手動新增頻道頁面模組
 */

//-----------------------------------------------------------------------
// 載入模組
import * as React from 'react';
import get from 'lodash/get';
import { Create, Edit, ReferenceInput, SelectInput, SimpleForm, TextInput, useRecordContext, usePermissions, Labeled, TopToolbar, ExportButton, CreateButton } from 'react-admin';
import { List, Show, SimpleShowLayout, Datagrid } from 'react-admin';
import { downloadCSV, DataProvider } from 'react-admin';
import { TextField, UrlField, EditButton, ShowButton, ReferenceField, DateField } from 'react-admin';
import jsonExport from 'jsonexport/dist';

//-----------------------------------------------------------------------
// defines
import { MANUALLY_CHANNEL_STATUS } from '../common/defines';
import { NamePairInfo } from '../common/interfaces';
import { MemberAuthInfo } from '../providers/authProvider';

//-----------------------------------------------------------------------
/** 新增頻道狀態顯示欄位元件。 */

const manuallyChannelStatusMap = new Map<string, string>();
const manuallyChannelStatusList : NamePairInfo<string>[] =
[
	{ id: MANUALLY_CHANNEL_STATUS.Waiting,		name: "等候加入" },
	{ id: MANUALLY_CHANNEL_STATUS.Finished,		name: "已完成" },
	{ id: MANUALLY_CHANNEL_STATUS.Failed,		name: "加入失敗" }
];
const manuallyChannelEditStatusList : NamePairInfo<string>[] =
[
	{ id: MANUALLY_CHANNEL_STATUS.Waiting,		name: "等候加入" },
	{ id: MANUALLY_CHANNEL_STATUS.Failed,		name: "加入失敗" }
];

for(let status of manuallyChannelStatusList)
	manuallyChannelStatusMap.set(status.id, status.name);

export const ManuallyChannelStatusField = (props: any) =>
{
	const { source } = props;
	const record = useRecordContext(props);

	return (<span className="MuiTypography-body2 MuiTypography-root">{manuallyChannelStatusMap.get(get(record, source))}</span>);
}

ManuallyChannelStatusField.defaultProps =
{
	label: '狀態',
	addLabel: true,
};

//-----------------------------------------------------------------------
/** 手動新增頻道列表編輯按鈕。 */
const ManuallyChannelEditButton = (props: any) =>
{
	const record = useRecordContext(props);

	if(props.edit && record?.status !== MANUALLY_CHANNEL_STATUS.Finished)
		return <EditButton {...props} />

	return <span/>;
}

//-----------------------------------------------------------------------
/** 手動新增頻道列表過濾器。 */
const channelFilters =
[
	<ReferenceInput key='input-source' label='資料來源' source='sourceId' reference='source' resettable alwaysOn >
		<SelectInput optionText='name' />
	</ReferenceInput>,
	<ReferenceInput key='input-keyword' label='關鍵字' source='keywordId' reference='keywordAll' alwaysOn >
		<SelectInput optionText='text' />
	</ReferenceInput>,
	<SelectInput key='input-status' label='狀態' source='status' choices={manuallyChannelStatusList} resettable />
];

const ExportActions = () => (
	<TopToolbar>
		<ExportButton maxResults={100000} />
	</TopToolbar>
);

const ListActions = () => (
	<TopToolbar>
		<CreateButton />
		<ExportButton maxResults={100000} />
	</TopToolbar>
);

/** 資料輸出功能。 */
const exporter = (records: any, fetchRelatedRecords: (data: any, field: string, resource: string) => Promise<any>, dataProvider: DataProvider) =>
{
	fetchRelatedRecords(records, 'sourceId', 'source').then((sources) =>
	{
		fetchRelatedRecords(records, 'keywordId', 'keyword').then((keywords) =>
		{
			const data = records.map((record: any) =>
			({
				...record,
				sourceId: sources[record.sourceId].name,
				keywordId: keywords[record.keywordId].text
			}));

			return jsonExport(data,
			{
				headers	: [ 'id', 'sourceId', 'keywordId', 'url', 'status', 'createTime' ],
				rename	: [ 'id', '資料來源', '關鍵字', '頻道網址', '狀態', '建立時間' ]
			}, (err, csv) =>
			{
				downloadCSV("\ufeff" + csv, 'manuallyChannel');
			});
		});
	});
};

/** 手動新增頻道列表頁面。 */
export const ManuallyChannelList = (props: any) =>
{
	let { permissions } = usePermissions();
	let	authInfo = permissions as MemberAuthInfo;

	return <List {...props} title='關鍵字' actions={authInfo.manuallyChannelCreate ? <ListActions /> : <ExportActions />} filters={channelFilters} exporter={exporter}>
		<Datagrid bulkActionButtons={false}>
			<TextField source='id' />
			<ReferenceField label="資料來源" source="sourceId" reference="source" >
				<TextField source="name" />
			</ReferenceField>
			<ReferenceField label="關鍵字" source="keywordId" reference="keyword" >
				<TextField source="text" />
			</ReferenceField>
			<UrlField source='url' label='頻道網址' target='_blank' />
			<ManuallyChannelStatusField source='status' />
			<DateField source='createTime' label='建立時間' showTime />
			<ManuallyChannelEditButton edit={authInfo.manuallyChannelEdit} />
			<ShowButton />
		</Datagrid>
	</List>;
};

/** 手動新增頻道資訊頁面。 */
export const ManuallyChannelShow = (props: any) => (
	<Show {...props}>
		<SimpleShowLayout>
			<TextField source='id' />
			<ReferenceField label="資料來源" source="sourceId" reference="source">
				<TextField source="name" />
			</ReferenceField>
			<ReferenceField label="關鍵字" source="keywordId" reference="keyword">
				<TextField source="text" />
			</ReferenceField>
			<UrlField source='url' label='頻道網址' target='_blank' />
			<ManuallyChannelStatusField source='status' />
			<DateField source='createTime' label='建立時間' showTime />
		</SimpleShowLayout>
	</Show>
);

/** 手動新增頻道編輯頁面。 */
export const ManuallyChannelEdit = (props: any) => (
	<Edit {...props}>
		<SimpleForm>
			<Labeled label="ID"><TextField source='id' /></Labeled>
			<ReferenceInput source="sourceId" reference="source" label="資料來源" >
				<SelectInput optionText="name" />
			</ReferenceInput>
			<ReferenceInput source="keywordId" reference="keywordAll" label="關鍵字" >
				<SelectInput optionText="text" />
			</ReferenceInput>
			<TextInput source='url' type='url' label="頻道網址" />
			<SelectInput source='status' choices={manuallyChannelEditStatusList} label="狀態" />
			<Labeled label="建立時間"><DateField source='createTime' showTime /></Labeled>
		</SimpleForm>
	</Edit>
);

/** 手動新增頻道建立頁面。 */
export const ManuallyChannelCreate = (props: any) => (
	<Create {...props}>
		<SimpleForm>
			<ReferenceInput source="sourceId" reference="source" label="資料來源" >
				<SelectInput optionText="name" />
			</ReferenceInput>
			<ReferenceInput source="keywordId" reference="keywordAll" label="關鍵字" >
				<SelectInput optionText="text" />
			</ReferenceInput>
			<TextInput source='url' type='url' label="頻道網址" />
		</SimpleForm>
	</Create>
);
